import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Check, X, Plus, Edit2, ChevronDown, ChevronUp, Trash2, GripVertical, Upload } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '@/components/GoogleMapsProvider';
import type { PropertyListings } from '@/types/PropertyListings';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import type { PendingMedias } from '@/types/PropertyListingMedias';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';

type FitMode = 'contain' | 'cover' | 'actual';

/** Default container ratio: 4:3 (height = 0.75 of width) — fills a printed two-column page the way the reference brochures do. */
const DEFAULT_ASPECT_HW = 3 / 4;
const MIN_ASPECT_HW = 0.2;   // very wide letterbox
const MAX_ASPECT_HW = 2.0;   // 1:2 portrait, height twice width

interface SectionImage {
    id: number | string;
    watermark: boolean;
    label?: string;
    fitMode?: FitMode;
    /** Container aspect ratio expressed as height / width (e.g. 0.5625 for 16:9). */
    aspectRatio?: number;
    /**
     * Image transform inside the container, in % of container box.
     * imgWidthPct / imgHeightPct can be > 100; imgLeftPct / imgTopPct can be < 0 or > 100 — image is allowed to overflow.
     */
    imgWidthPct?: number;
    imgHeightPct?: number;
    imgLeftPct?: number;
    imgTopPct?: number;
    /** Legacy fields kept for backwards-compat on read. */
    customHeight?: number;
    frameWidthPercent?: number;
    positionX?: number;
    positionY?: number;
    imageWidthPercent?: number;
}

interface Feature {
    id: string;
    label: string;
    value: string;
}

interface FeatureCategory {
    id: string;
    label: string;
    features: Feature[];
}

interface Section {
    id: string;
    title: string;
    description: string;
    type?: string;
    images?: SectionImage[];
    editable?: boolean;
    deletable?: boolean;
    collapsed?: boolean;
    custom?: boolean;
    isNew?: boolean;
    featureCategories?: FeatureCategory[];
    useGoogleMaps?: boolean;
    mapZoom?: number;
    propertyTitle?: string;
    propertyDescription?: string;
}

/**
 * Minimal agent watermark preview — mirrors the overlay the API prints on every
 * proposal image (views/proposalTemplate.js) and the watermark xpillar bakes onto
 * listing photos: small white name with a soft shadow, dim REN below, lower-right.
 */
const ProposalWatermark: React.FC<{ name: string; ren?: string }> = ({ name, ren }) => (
    <div
        className="pointer-events-none absolute z-10 text-right text-white"
        style={{
            right: '3.2cqw',
            bottom: '2.8cqw',
            maxWidth: 'calc(100% - 6cqw)',
            fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            lineHeight: 1.25,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.35)',
        }}
    >
        <span
            style={{
                display: 'block',
                fontSize: 'clamp(9px, 3.2cqw, 22px)',
                fontWeight: 700,
                letterSpacing: '0.4px',
                whiteSpace: 'nowrap',
            }}
        >
            {name}
        </span>
        {ren && (
            <span
                style={{
                    display: 'block',
                    fontSize: 'clamp(7px, 1.8cqw, 13px)',
                    fontWeight: 500,
                    letterSpacing: '0.3px',
                    color: '#e5e7eb',
                    opacity: 0.92,
                    whiteSpace: 'nowrap',
                }}
            >
                {ren}
            </span>
        )}
    </div>
);

interface _ProposalConfiguration {
    listing: PropertyListings;
    proposal: any;
    setProposal: React.Dispatch<React.SetStateAction<any>>;
    sections: Section[];
    setSections: React.Dispatch<React.SetStateAction<Section[]>>;
}

const ProposalConfiguration: React.FC<_ProposalConfiguration> = ({
    listing,
    proposal: _proposal,
    setProposal: _setProposal,
    sections,
    setSections
}) => {
    const { isLoaded } = useGoogleMaps();
    const agent = useSelector((state: RootState) => state.auth.agent);
    const watermarkName = [agent?.firstName, agent?.lastName].filter(Boolean).join(' ').trim() || 'SALT & LIGHT REALTY';
    const watermarkRen = agent?.renNo ? `REN: ${agent.renNo}` : '';
    const dragCounterRef = useRef(0);
    const [proposalListing, setProposalListing] = useState<PropertyListings>(listing);
    const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
    const [showSectionDialog, setShowSectionDialog] = useState(false);
    const [editingSection, setEditingSection] = useState<Partial<Section> | null>(null);
    const [dialogImages, setDialogImages] = useState<SectionImage[]>([]);
    const [draggedSection, setDraggedSection] = useState<string | null>(null);
    const [dragOverSection, setDragOverSection] = useState<string | null>(null);
    const [draggedImage, setDraggedImage] = useState<{ sectionId: string; imageId: string | number } | null>(null);
    const [dragOverImage, setDragOverImage] = useState<{ sectionId: string; imageId: string | number } | null>(null);
    const [isSaving] = useState<boolean>(false);
    const [activeAdjustImage, setActiveAdjustImage] = useState<{ sectionId: string; imageId: string | number } | null>(null);

    const clampAspectHW = (v?: number) => {
        const n = typeof v === 'number' && !Number.isNaN(v) ? v : DEFAULT_ASPECT_HW;
        return Math.min(MAX_ASPECT_HW, Math.max(MIN_ASPECT_HW, n));
    };

    /** Pick the aspect ratio for an image, falling back to legacy customHeight or the 16:9 default. */
    const getAspectHW = (img: SectionImage, fallbackWidthPx = 720): number => {
        if (typeof img.aspectRatio === 'number') return clampAspectHW(img.aspectRatio);
        // Legacy migration: customHeight (px) / fallback width gives an approximate ratio.
        if (typeof img.customHeight === 'number' && img.customHeight > 0) {
            return clampAspectHW(img.customHeight / fallbackWidthPx);
        }
        return DEFAULT_ASPECT_HW;
    };

    // Image inside the container can extend up to this far past the container edge in either direction.
    const MAX_IMG_OVERFLOW_PCT = 80;
    const MIN_IMG_SIZE_PCT = 10;
    const MAX_IMG_SIZE_PCT = 400;

    const clampImgWidth = (v?: number) => {
        const n = typeof v === 'number' && !Number.isNaN(v) ? v : 100;
        return Math.min(MAX_IMG_SIZE_PCT, Math.max(MIN_IMG_SIZE_PCT, n));
    };
    const clampImgHeight = (v?: number) => {
        const n = typeof v === 'number' && !Number.isNaN(v) ? v : 100;
        return Math.min(MAX_IMG_SIZE_PCT, Math.max(MIN_IMG_SIZE_PCT, n));
    };
    const clampImgOffset = (v?: number) => {
        const n = typeof v === 'number' && !Number.isNaN(v) ? v : 0;
        return Math.min(100 + MAX_IMG_OVERFLOW_PCT, Math.max(-MAX_IMG_OVERFLOW_PCT, n));
    };

    /** Cache for natural image dimensions, keyed by media URL. */
    const naturalSizes = useRef<Map<string, { w: number; h: number }>>(new Map());
    const [naturalSizesVersion, setNaturalSizesVersion] = useState(0);

    const recordNatural = (src: string, w: number, h: number) => {
        if (!src || !w || !h) return;
        const prev = naturalSizes.current.get(src);
        if (prev && prev.w === w && prev.h === h) return;
        naturalSizes.current.set(src, { w, h });
        setNaturalSizesVersion(v => v + 1);
    };

    /**
     * Resolve concrete transform values for an image (in container %).
     * - If imgWidthPct/imgHeightPct/imgLeftPct/imgTopPct are stored, use them.
     * - Otherwise derive from fitMode + natural aspect ratio.
     */
    const resolveImageTransform = (img: SectionImage, src: string, containerHeightPx: number, containerWidthPx?: number) => {
        // Compute fit-mode defaults from natural size below, then override individual
        // fields with anything the editor has stored (drag/zoom/resize results).
        const hasAny = img.imgWidthPct !== undefined || img.imgHeightPct !== undefined
            || img.imgLeftPct !== undefined || img.imgTopPct !== undefined;
        const allStored = img.imgWidthPct !== undefined && img.imgHeightPct !== undefined
            && img.imgLeftPct !== undefined && img.imgTopPct !== undefined;
        if (allStored) {
            return {
                widthPct: clampImgWidth(img.imgWidthPct),
                heightPct: clampImgHeight(img.imgHeightPct),
                leftPct: clampImgOffset(img.imgLeftPct),
                topPct: clampImgOffset(img.imgTopPct),
            };
        }
        void hasAny;
        const fit: FitMode = (img.fitMode ?? 'contain') as FitMode;
        const natural = naturalSizes.current.get(src);

        // Compute base values from fit-mode + natural aspect.
        let base: { widthPct: number; heightPct: number; leftPct: number; topPct: number };
        if (!natural) {
            base = { widthPct: 100, heightPct: 100, leftPct: 0, topPct: 0 };
        } else {
            const cW = containerWidthPx || 1;
            const cH = containerHeightPx || 1;
            const naturalAspect = natural.w / natural.h;
            const containerAspect = cW / cH;
            if (fit === 'actual') {
                const heightPct = (cW / naturalAspect) / cH * 100;
                base = { widthPct: 100, heightPct, leftPct: 0, topPct: (100 - heightPct) / 2 };
            } else if (fit === 'cover') {
                if (naturalAspect > containerAspect) {
                    const widthPct = (cH * naturalAspect) / cW * 100;
                    base = { widthPct, heightPct: 100, leftPct: (100 - widthPct) / 2, topPct: 0 };
                } else {
                    const heightPct = (cW / naturalAspect) / cH * 100;
                    base = { widthPct: 100, heightPct, leftPct: 0, topPct: (100 - heightPct) / 2 };
                }
            } else if (naturalAspect > containerAspect) {
                const heightPct = (cW / naturalAspect) / cH * 100;
                base = { widthPct: 100, heightPct, leftPct: 0, topPct: (100 - heightPct) / 2 };
            } else {
                const widthPct = (cH * naturalAspect) / cW * 100;
                base = { widthPct, heightPct: 100, leftPct: (100 - widthPct) / 2, topPct: 0 };
            }
        }

        // Apply any partial overrides from the stored transform (drag/zoom/resize).
        return {
            widthPct: clampImgWidth(img.imgWidthPct ?? base.widthPct),
            heightPct: clampImgHeight(img.imgHeightPct ?? base.heightPct),
            leftPct: clampImgOffset(img.imgLeftPct ?? base.leftPct),
            topPct: clampImgOffset(img.imgTopPct ?? base.topPct),
        };
    };

    const updateImageDisplay = useCallback((
        sectionId: string,
        imageId: string | number,
        patch: Partial<SectionImage>
    ) => {
        setSections(prev => prev.map(section => {
            if (section.id !== sectionId) return section;
            return {
                ...section,
                images: section.images?.map(img => (img.id === imageId ? { ...img, ...patch } : img)),
            };
        }));
    }, [setSections]);

    /** Active drag operation: moving the image or resizing from a corner. */
    type DragOp =
        | { kind: 'move'; sectionId: string; imageId: string | number; containerW: number; containerH: number; startClientX: number; startClientY: number; startLeftPct: number; startTopPct: number }
        | { kind: 'resize'; sectionId: string; imageId: string | number; corner: 'nw' | 'ne' | 'sw' | 'se'; containerW: number; containerH: number; startClientX: number; startClientY: number; startWidthPct: number; startHeightPct: number; startLeftPct: number; startTopPct: number; aspect: number };
    const dragRef = useRef<DragOp | null>(null);

    /** Begin moving the entire image inside the container (or beyond it). */
    const beginImageMove = (e: React.PointerEvent, sectionId: string, img: SectionImage, containerEl: HTMLElement) => {
        if (e.button !== 0) return;
        const rect = containerEl.getBoundingClientRect();
        const src = (() => {
            const sec = sections.find(s => s.id === sectionId);
            const media = sec?.images && (proposalListing.medias || []).find(m => m.id === img.id);
            return media ? getMediaUrl('path' in media ? (media.path as string) : '') : '';
        })();
        const t = resolveImageTransform(img, src, rect.height, rect.width);
        dragRef.current = {
            kind: 'move',
            sectionId,
            imageId: img.id,
            containerW: rect.width,
            containerH: rect.height,
            startClientX: e.clientX,
            startClientY: e.clientY,
            startLeftPct: t.leftPct,
            startTopPct: t.topPct,
        };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    };

    /** Begin resizing from one of the four corners; preserves aspect ratio. */
    const beginCornerResize = (e: React.PointerEvent, sectionId: string, img: SectionImage, corner: 'nw' | 'ne' | 'sw' | 'se', containerEl: HTMLElement, src: string) => {
        e.stopPropagation();
        if (e.button !== 0) return;
        const rect = containerEl.getBoundingClientRect();
        const t = resolveImageTransform(img, src, rect.height, rect.width);
        const aspect = t.widthPct === 0 || t.heightPct === 0
            ? (naturalSizes.current.get(src)?.w || 1) / (naturalSizes.current.get(src)?.h || 1)
            : (t.widthPct * rect.width) / (t.heightPct * rect.height);
        dragRef.current = {
            kind: 'resize',
            sectionId,
            imageId: img.id,
            corner,
            containerW: rect.width,
            containerH: rect.height,
            startClientX: e.clientX,
            startClientY: e.clientY,
            startWidthPct: t.widthPct,
            startHeightPct: t.heightPct,
            startLeftPct: t.leftPct,
            startTopPct: t.topPct,
            aspect,
        };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onDragMove = (e: React.PointerEvent) => {
        const d = dragRef.current;
        if (!d) return;
        const dx = e.clientX - d.startClientX;
        const dy = e.clientY - d.startClientY;
        const dxPct = (dx / d.containerW) * 100;
        const dyPct = (dy / d.containerH) * 100;

        if (d.kind === 'move') {
            updateImageDisplay(d.sectionId, d.imageId, {
                imgLeftPct: clampImgOffset(d.startLeftPct + dxPct),
                imgTopPct: clampImgOffset(d.startTopPct + dyPct),
            });
            return;
        }

        // Resize keeps aspect ratio. Drive width by the dominant axis based on which corner is dragged.
        const dirX = d.corner === 'ne' || d.corner === 'se' ? 1 : -1;
        const dirY = d.corner === 'sw' || d.corner === 'se' ? 1 : -1;
        const widthCandidate = d.startWidthPct + dirX * dxPct;
        const heightFromWidth = widthCandidate * d.aspect * (d.containerW / d.containerH) === 0
            ? d.startHeightPct
            : widthCandidate / ((d.containerW / d.containerH) / d.aspect);
        // Simpler: image natural aspect = naturalW/naturalH = aspect; in container-% terms keep widthPx/heightPx ratio == aspect.
        // widthPx = widthPct * containerW / 100; heightPx = heightPct * containerH / 100.
        // aspect = widthPx / heightPx => heightPct = (widthPct * containerW) / (aspect * containerH).
        const newWidthPct = clampImgWidth(widthCandidate);
        const newHeightPct = clampImgHeight((newWidthPct * d.containerW) / (d.aspect * d.containerH));
        // Anchor the opposite corner so the dragged corner moves under the cursor.
        const widthDelta = newWidthPct - d.startWidthPct;
        const heightDelta = newHeightPct - d.startHeightPct;
        let leftPct = d.startLeftPct;
        let topPct = d.startTopPct;
        if (dirX === -1) leftPct = d.startLeftPct - widthDelta;
        if (dirY === -1) topPct = d.startTopPct - heightDelta;
        updateImageDisplay(d.sectionId, d.imageId, {
            imgWidthPct: newWidthPct,
            imgHeightPct: newHeightPct,
            imgLeftPct: clampImgOffset(leftPct),
            imgTopPct: clampImgOffset(topPct),
        });
        // Touch heightFromWidth so the linter doesn't trip; we kept the derivation comment-paired.
        void heightFromWidth;
    };

    const endDrag = (e: React.PointerEvent) => {
        if (dragRef.current) {
            try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
            dragRef.current = null;
        }
    };

    const resetImageLayout = (sectionId: string, imageId: string | number) => {
        updateImageDisplay(sectionId, imageId, {
            imgWidthPct: undefined,
            imgHeightPct: undefined,
            imgLeftPct: undefined,
            imgTopPct: undefined,
            aspectRatio: DEFAULT_ASPECT_HW,
            fitMode: 'cover',
        });
    };

    const applyFitMode = (sectionId: string, imageId: string | number, fit: FitMode) => {
        // Switching fit mode clears stored transform so the new mode is recomputed from natural size.
        updateImageDisplay(sectionId, imageId, {
            fitMode: fit,
            imgWidthPct: undefined,
            imgHeightPct: undefined,
            imgLeftPct: undefined,
            imgTopPct: undefined,
        });
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const getMediaUrl = (path: string) => {
        if (!path || typeof path !== 'string') return '';
        const p = path.trim();
        if (!p) return '';
        if (p.startsWith('blob:') || p.startsWith('http')) {
            return p;
        }
        const base = `${import.meta.env.VITE_AWS_S3 || ''}`.replace(/\/$/, '');
        return base ? `${base}/${p.replace(/^\//, '')}` : p;
    };

    const processFilesAndUpload = async (files: File[]) => {
        if (!files.length) return;
    
        const allowedFiles = Array.from(files).filter(file => {
            const fileExt = file.name.split('.').pop()?.toLowerCase();
            return fileExt === 'jpg' || fileExt === 'jpeg' || fileExt === 'png';
        });
    
        if (!allowedFiles.length) {
            alert('Only JPG, JPEG and PNG files are allowed.');
            return;
        }
    
        const tempFiles: PendingMedias[] = allowedFiles.map(file => ({
            id: `temp-${Date.now()}-${Math.random()}-${file.name}`,
            url: URL.createObjectURL(file),
            uploading: true,
            mediaType: 1,
            file,
            error: false,
            path: URL.createObjectURL(file)
        }));
    
        setProposalListing(prev => ({
            ...prev,
            medias: [...(prev.medias || []), ...tempFiles as any]
        }));
    
        try {
            const formData = new FormData();
            formData.append('type', '1');
            allowedFiles.forEach(file => formData.append("files", file));
    
            const response = await api.post(
                `/listings/${listing.id}/medias/hidden`,
                formData,
                { 
                    headers: { 
                        'Content-Type': 'multipart/form-data' 
                    } 
                }
            );
            if (!response.data.status) throw new Error(response.data.error || 'Upload failed');
    
            // Handle rejected files
            if (response.data.rejectedFiles?.length > 0) {
                response.data.rejectedFiles.forEach((rejected: any) => {
                    toast.error(`${rejected.filename}: ${rejected.reason}`);
                });
            }
    
            setProposalListing(prev => ({
                ...prev,
                medias: prev.medias.map((m: any) => {
                    if (m.uploading && m.file) {
                        // Check if rejected
                        const isRejected = response.data.rejectedFiles?.some(
                            (r: any) => r.filename === m.file.name
                        );
                        
                        if (isRejected) {
                            return { ...m, uploading: false, error: true };
                        }
                        
                        // Get next uploaded file in order
                        const uploaded = response.data.files.shift();
                        if (uploaded) {
                            return { 
                                ...uploaded, 
                                dataValues: uploaded 
                            };
                        }
                    }
                    return m;
                })
            }));
        } catch (err) {
            console.error('Upload error:', err);
            alert('Upload failed');
    
            setProposalListing(prev => ({
                ...prev,
                medias: (prev.medias || []).map((m: any) =>
                    m.file ? { ...m, uploading: false, error: true } : m
                )
            }));
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            processFilesAndUpload(Array.from(files));
        }
        e.target.value = '';
    };
    
    const retryUpload = (pending: any) => {
        setProposalListing(prev => ({
            ...prev,
            medias: (prev.medias || []).map((m: any) =>
                m.id === pending.id ? { ...pending, uploading: true, error: false } : m
            )
        }));
        if (pending.file) {
            processFilesAndUpload([pending.file]);
        }
    };

    // Specification feature management
    const addFeatureCategory = (sectionId: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    featureCategories: [
                        ...(section.featureCategories || []),
                        {
                            id: `category-${Date.now()}`,
                            label: 'New Category',
                            features: []
                        }
                    ]
                };
            }
            return section;
        }));
    };

    const updateCategoryLabel = (sectionId: string, categoryId: string, label: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    featureCategories: section.featureCategories?.map(cat =>
                        cat.id === categoryId ? { ...cat, label } : cat
                    )
                };
            }
            return section;
        }));
    };

    const deleteFeatureCategory = (sectionId: string, categoryId: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    featureCategories: section.featureCategories?.filter(cat => cat.id !== categoryId)
                };
            }
            return section;
        }));
    };

    const addFeatureToCategory = (sectionId: string, categoryId: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    featureCategories: section.featureCategories?.map(cat =>
                        cat.id === categoryId ? {
                            ...cat,
                            features: [
                                ...cat.features,
                                { id: `feature-${Date.now()}`, label: '', value: '' }
                            ]
                        } : cat
                    )
                };
            }
            return section;
        }));
    };

    const updateFeature = (sectionId: string, categoryId: string, featureId: string, field: keyof Feature, value: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    featureCategories: section.featureCategories?.map(cat =>
                        cat.id === categoryId ? {
                            ...cat,
                            features: cat.features.map(feature =>
                                feature.id === featureId ? { ...feature, [field]: value } : feature
                            )
                        } : cat
                    )
                };
            }
            return section;
        }));
    };

    const deleteFeature = (sectionId: string, categoryId: string, featureId: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    featureCategories: section.featureCategories?.map(cat =>
                        cat.id === categoryId ? {
                            ...cat,
                            features: cat.features.filter(f => f.id !== featureId)
                        } : cat
                    )
                };
            }
            return section;
        }));
    };

    const toggleSection = (sectionId: string) => {
        setSections(prev => prev.map(section =>
            section.id === sectionId ? { ...section, collapsed: !section.collapsed } : section
        ));
    };

    const toggleImageSelection = (sectionId: string, imageId: string | number) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                const images = section.images || [];
                const exists = images.find(img => img.id === imageId);

                if (exists) {
                    return {
                        ...section,
                        images: images.filter(img => img.id !== imageId)
                    };
                } else {
                    return {
                        ...section,
                        images: [...images, {
                            id: imageId,
                            watermark: true,
                            label: '',
                            fitMode: 'cover',
                            aspectRatio: DEFAULT_ASPECT_HW,
                        }]
                    };
                }
            }
            return section;
        }));
    };

    const toggleImageWatermark = (sectionId: string, imageId: string | number) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    images: section.images?.map(img =>
                        img.id === imageId ? { ...img, watermark: !img.watermark } : img
                    )
                };
            }
            return section;
        }));
    };

    const updateImageLabel = (sectionId: string, imageId: string | number, label: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    images: section.images?.map(img =>
                        img.id === imageId ? { ...img, label } : img
                    )
                };
            }
            return section;
        }));
    };

    const toggleDialogImage = (imageId: string | number) => {
        setDialogImages(prev => {
            const exists = prev.find(img => img.id === imageId);
            if (exists) {
                return prev.filter(img => img.id !== imageId);
            } else {
                return [...prev, {
                    id: imageId,
                    watermark: true,
                    label: '',
                    fitMode: 'cover',
                    aspectRatio: DEFAULT_ASPECT_HW,
                }];
            }
        });
    };

    const toggleDialogImageWatermark = (imageId: string | number) => {
        setDialogImages(prev => prev.map(img =>
            img.id === imageId ? { ...img, watermark: !img.watermark } : img
        ));
    };

    const updateDialogImageLabel = (imageId: string | number, label: string) => {
        setDialogImages(prev => prev.map(img =>
            img.id === imageId ? { ...img, label } : img
        ));
    };

    const removeDialogImage = (imageId: string | number) => {
        setDialogImages(prev => prev.filter(img => img.id !== imageId));
    };

    const openAddSectionDialog = () => {
        const newSection: Section = {
            id: `section-${Date.now()}`,
            title: '',
            description: '',
            images: [],
            editable: true,
            deletable: true,
            custom: true,
            isNew: true
        };
        setSections(prev => [...prev, newSection]);

        setTimeout(() => {
            const element = document.getElementById(newSection.id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    const openEditSectionDialog = (section: Section) => {
        setEditingSection({
            id: section.id,
            title: section.title,
            description: section.description
        });
        setDialogImages([...(section.images || [])]);
        setShowSectionDialog(true);
    };

    const saveSectionDialog = () => {
        if (editingSection?.id) {
            setSections(prev => prev.map(section =>
                section.id === editingSection.id
                    ? {
                        ...section,
                        title: editingSection.title || section.title,
                        description: editingSection.description || section.description,
                        images: dialogImages
                    }
                    : section
            ));
        }
        closeDialog();
    };

    const closeDialog = () => {
        setShowSectionDialog(false);
        setEditingSection(null);
        setDialogImages([]);
    };

    const deleteSection = (sectionId: string) => {
        setSections(prev => prev.filter(s => s.id !== sectionId));
    };

    const updateSection = (sectionId: string, field: keyof Section, value: any) => {
        setSections(prev => prev.map(section =>
            section.id === sectionId ? { ...section, [field]: value } : section
        ));
    };

    const handleFileDragEnter = useCallback((e: React.DragEvent, _sectionId: string) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current++;
    }, []);

    const handleFileDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current--;
    }, []);

    const handleFileDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleFileDrop = useCallback((e: React.DragEvent, _sectionId: string) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
    }, []);

    // Section drag and drop
    const handleSectionDragStart = (e: React.DragEvent, sectionId: string) => {
        setDraggedSection(sectionId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleSectionDragOver = (e: React.DragEvent, sectionId: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedSection && draggedSection !== sectionId) {
            setDragOverSection(sectionId);
        }
    };

    const handleSectionDragLeave = () => {
        setDragOverSection(null);
    };

    const handleSectionDrop = (e: React.DragEvent, targetSectionId: string) => {
        e.preventDefault();

        if (!draggedSection || draggedSection === targetSectionId) {
            setDraggedSection(null);
            setDragOverSection(null);
            return;
        }

        setSections(prev => {
            const draggedIndex = prev.findIndex(s => s.id === draggedSection);
            const targetIndex = prev.findIndex(s => s.id === targetSectionId);

            if (draggedIndex === -1 || targetIndex === -1) return prev;

            const newSections = [...prev];
            const [removed] = newSections.splice(draggedIndex, 1);
            newSections.splice(targetIndex, 0, removed);

            return newSections;
        });

        setDraggedSection(null);
        setDragOverSection(null);
    };

    const handleSectionDragEnd = () => {
        setDraggedSection(null);
        setDragOverSection(null);
    };

    // Image drag and drop for reordering
    const handleImageDragStart = (e: React.DragEvent, sectionId: string, imageId: string | number) => {
        e.stopPropagation();
        setDraggedImage({ sectionId, imageId });
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleImageDragOver = (e: React.DragEvent, sectionId: string, imageId: string | number) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';

        if (draggedImage &&
            draggedImage.sectionId === sectionId &&
            draggedImage.imageId !== imageId) {
            setDragOverImage({ sectionId, imageId });
        }
    };

    const handleImageDragLeave = (e: React.DragEvent) => {
        e.stopPropagation();
        setDragOverImage(null);
    };

    const handleImageDrop = (e: React.DragEvent, sectionId: string, targetImageId: string | number) => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedImage ||
            draggedImage.sectionId !== sectionId ||
            draggedImage.imageId === targetImageId) {
            setDraggedImage(null);
            setDragOverImage(null);
            return;
        }

        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                const images = [...(section.images || [])];
                const draggedIndex = images.findIndex(img => img.id === draggedImage.imageId);
                const targetIndex = images.findIndex(img => img.id === targetImageId);

                if (draggedIndex === -1 || targetIndex === -1) return section;

                [images[draggedIndex], images[targetIndex]] = [images[targetIndex], images[draggedIndex]];

                return { ...section, images };
            }
            return section;
        }));

        setDraggedImage(null);
        setDragOverImage(null);
    };

    const handleImageDragEnd = (e: React.DragEvent) => {
        e.stopPropagation();
        setDraggedImage(null);
        setDragOverImage(null);
    };

    const renderSectionContent = (section: Section) => {
        if (section.type === 'cover') {
            return (
                <div className="space-y-6">
                    {Boolean(section.images?.length) && (
                        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-4">
                            <Label className="mb-1 block text-sm font-medium">Cover layout &amp; crop</Label>
                            <p className="mb-3 text-xs text-muted-foreground">
                                Adjust the frame and image first, then pick a different photo below if needed.
                            </p>
                            {renderImageGrid(section, { omitAvailableGrid: true, omitSelectedImagesLabel: true, singleColumn: true })}
                        </div>
                    )}
                    <div>
                        <Label className="mb-3 block text-sm text-muted-foreground">Choose cover image</Label>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                            {(proposalListing.medias || []).map((media) => {
                                const isSelected = section.images?.some(img => img.id === media.id);
                                return (
                                    <div
                                        key={media.id}
                                        onClick={() => {
                                            setSections(prev => prev.map(s =>
                                                s.id === section.id ? { ...s, images: [{
                                                    id: media.id,
                                                    watermark: true,
                                                    fitMode: 'cover',
                                                    aspectRatio: DEFAULT_ASPECT_HW,
                                                }] } : s
                                            ));
                                        }}
                                        className={`relative aspect-video cursor-pointer overflow-hidden rounded-lg border-2 transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'
                                            }`}
                                    >
                                        <div
                                            className="h-full w-full bg-cover bg-center"
                                            style={{ backgroundImage: `url(${getMediaUrl('path' in media ? media.path : '')})` }}
                                        />
                                        {isSelected && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-primary/20">
                                                <div className="rounded-full bg-primary p-2 text-primary-foreground">
                                                    <Check size={20} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            );
        }

        if (section.type === 'specifications') {
            return (
                <div className="space-y-6">
                    {section.featureCategories?.map((category) => (
                        <div key={category.id} className="border rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <Input
                                    value={category.label}
                                    onChange={(e) => updateCategoryLabel(section.id, category.id, e.target.value)}
                                    className="font-semibold text-base max-w-xs"
                                />
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => addFeatureToCategory(section.id, category.id)}
                                    >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Feature
                                    </Button>
                                    {!['basic', 'industrial'].includes(category.id) && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => deleteFeatureCategory(section.id, category.id)}
                                        >
                                            <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-2">
                                {category.features.map((feature) => (
                                    <div key={feature.id} className="flex items-start gap-2">
                                        <Textarea
                                            placeholder="Label"
                                            value={feature.label}
                                            onChange={(e) =>
                                                updateFeature(section.id, category.id, feature.id, 'label', e.target.value)
                                            }
                                            className="flex-1 min-h-[40px] resize-y"
                                            rows={1}
                                        />
                                        <Textarea
                                            placeholder="Value"
                                            value={feature.value}
                                            onChange={(e) =>
                                                updateFeature(section.id, category.id, feature.id, 'value', e.target.value)
                                            }
                                            className="flex-1 min-h-[40px] resize-y"
                                            rows={1}
                                        />
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => deleteFeature(section.id, category.id, feature.id)}
                                            className="mt-1"
                                        >
                                            <X className="h-4 w-4 text-red-500" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    <Button
                        variant="outline"
                        onClick={() => addFeatureCategory(section.id)}
                        className="w-full"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Feature Category
                    </Button>
                </div>
            );
        }

        if (section.type === 'overview') {
            return (
                <div className="space-y-4">
                    {/* <div>
                        <Label className="mb-2 block">Property Title</Label>
                        <Input
                            value={section.propertyTitle || ''}
                            onChange={(e) => updateSection(section.id, 'propertyTitle', e.target.value)}
                            placeholder="Enter property title..."
                            className="font-semibold"
                        />
                    </div> */}
                    <div>
                        <Label className="mb-2 block">Property Description</Label>
                        <Textarea
                            value={section.propertyDescription || ''}
                            onChange={(e) => updateSection(section.id, 'propertyDescription', e.target.value)}
                            placeholder="Enter property description which includes advantage or disadvantage..."
                            rows={6}
                        />
                    </div>
                </div>
            );
        }

        if (section.id === 'location') {
            return (
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={section.useGoogleMaps}
                            onCheckedChange={(checked) => updateSection(section.id, 'useGoogleMaps', checked)}
                        />
                        <Label>Include Google Maps</Label>
                    </div>
                    {section.useGoogleMaps && isLoaded && (
                        <div className="w-full rounded-lg overflow-hidden">
                            <GoogleMap
                            mapContainerStyle={{
                                width: "100%",
                                height: "300px",
                                borderRadius: "0.5rem"
                            }}
                            center={proposalListing.lat && proposalListing.lng ? {
                                lat: Number(proposalListing.lat),
                                lng: Number(proposalListing.lng)
                            } : {
                                lat: 3.139,
                                lng: 101.6869
                            }}
                            zoom={15}
                            options={{
                                mapTypeControl: false,
                                streetViewControl: false,
                                fullscreenControl: false,
                                zoomControl: true,
                                mapTypeId: "satellite",
                                styles: [
                                    {
                                        featureType: "poi",
                                        elementType: "labels",
                                        stylers: [{ visibility: "off" }],
                                    },
                                    {
                                        featureType: "transit",
                                        elementType: "labels",
                                        stylers: [{ visibility: "off" }],
                                    }
                                ],
                            }}>
                                {proposalListing.lat && proposalListing.lng && (
                                    <Marker position={{
                                        lat: Number(proposalListing.lat),
                                        lng: Number(proposalListing.lng)
                                    }} />
                                )}
                            </GoogleMap>
                        </div>
                    )}

                    {renderImageGrid(section)}
                </div>
            );
        }

        return renderImageGrid(section);
    };

    type ImageGridOptions = {
        /** Hide the library strip (cover uses its own picker above). */
        omitAvailableGrid?: boolean;
        /** Hide “Selected Images (n)” — e.g. cover uses a single layout block. */
        omitSelectedImagesLabel?: boolean;
        /** Force single-column layout so the preview mirrors a full-page image in the final proposal. */
        singleColumn?: boolean;
    };

    const renderImageGrid = (section: Section, opts?: ImageGridOptions) => {
        const selectedImages = section.images || [];
        const availableImages = (proposalListing.medias || []).filter(
            media => !selectedImages.some(img => img.id === media.id)
        );

        return (
            <div
                onDragEnter={(e) => handleFileDragEnter(e, section.id)}
                onDragOver={handleFileDragOver}
                onDragLeave={handleFileDragLeave}
                onDrop={(e) => handleFileDrop(e, section.id)}
            >
                <div className="space-y-4">
                    {selectedImages.length > 0 && (
                        <div>
                            {!opts?.omitSelectedImagesLabel && (
                                <Label className="mb-3 block">Selected Images ({selectedImages.length})</Label>
                            )}
                            <div className={`grid gap-4 ${opts?.singleColumn ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                                {selectedImages.map((img) => {
                                    const media = (proposalListing.medias || []).find(m => m.id === img.id);
                                    if (!media) return null;

                                    const isDragging = draggedImage?.sectionId === section.id && draggedImage?.imageId === img.id;
                                    const isDropTarget = dragOverImage?.sectionId === section.id && dragOverImage?.imageId === img.id;
                                    const isAdjusting = activeAdjustImage?.sectionId === section.id && activeAdjustImage?.imageId === img.id;

                                    return (
                                        <div
                                            key={img.id}
                                            className={`min-w-0 overflow-hidden rounded-lg border p-3 space-y-2 transition-all ${isDragging ? 'opacity-50' : ''
                                                } ${isDropTarget ? 'border-primary border-2 ring-2 ring-primary/20' : ''
                                                }`}
                                            onDragOver={(e) => handleImageDragOver(e, section.id, img.id)}
                                            onDragLeave={handleImageDragLeave}
                                            onDrop={(e) => handleImageDrop(e, section.id, img.id)}
                                        >
                                            <div className="relative overflow-visible rounded-xl bg-white shadow-sm" style={{ containerType: 'inline-size' } as React.CSSProperties}>
                                                <div className="relative w-full">
                                                    <ImageEditor
                                                        src={getMediaUrl('path' in media ? media.path : '')}
                                                        alt={img.label || 'Proposal image'}
                                                        img={img}
                                                        aspectHW={getAspectHW(img)}
                                                        onAspectHWChange={(v) => updateImageDisplay(section.id, img.id, { aspectRatio: clampAspectHW(v) })}
                                                        focused={isAdjusting}
                                                        onToggleFocus={() => setActiveAdjustImage(isAdjusting ? null : { sectionId: section.id, imageId: img.id })}
                                                        onLoadNatural={recordNatural}
                                                        resolveTransform={(im, s, cH, cW) => resolveImageTransform(im, s, cH, cW)}
                                                        onMoveStart={(e, containerEl) => beginImageMove(e, section.id, img, containerEl)}
                                                        onCornerStart={(e, corner, containerEl) => beginCornerResize(e, section.id, img, corner, containerEl, getMediaUrl('path' in media ? media.path : ''))}
                                                        onPointerMove={onDragMove}
                                                        onPointerUp={endDrag}
                                                        onWheelZoom={(e, containerEl) => {
                                                            if (!e.ctrlKey && !e.metaKey) return;
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            const rect = containerEl.getBoundingClientRect();
                                                            const src = getMediaUrl('path' in media ? media.path : '');
                                                            const t = resolveImageTransform(img, src, rect.height, rect.width);
                                                            const factor = e.deltaY > 0 ? 0.94 : 1.06;
                                                            const newWidthPct = clampImgWidth(t.widthPct * factor);
                                                            const newHeightPct = clampImgHeight(t.heightPct * factor);
                                                            // Zoom around the cursor point.
                                                            const px = ((e.clientX - rect.left) / rect.width) * 100;
                                                            const py = ((e.clientY - rect.top) / rect.height) * 100;
                                                            const newLeft = px - ((px - t.leftPct) * (newWidthPct / t.widthPct));
                                                            const newTop = py - ((py - t.topPct) * (newHeightPct / t.heightPct));
                                                            updateImageDisplay(section.id, img.id, {
                                                                imgWidthPct: newWidthPct,
                                                                imgHeightPct: newHeightPct,
                                                                imgLeftPct: clampImgOffset(newLeft),
                                                                imgTopPct: clampImgOffset(newTop),
                                                            });
                                                        }}
                                                        // re-render whenever natural size becomes known
                                                        version={naturalSizesVersion}
                                                    />
                                                    <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center">
                                                        <div className={`rounded-full bg-black/60 px-3 py-1 text-[11px] text-white transition-opacity ${isAdjusting ? 'opacity-100' : 'opacity-70'}`}>
                                                            {isAdjusting
                                                                ? 'Drag image to move. Corners resize. Cmd/Ctrl + wheel to zoom.'
                                                                : 'Double-click to adjust'}
                                                        </div>
                                                    </div>
                                                </div>
                                                {img.watermark && <ProposalWatermark name={watermarkName} ren={watermarkRen} />}
                                                {img.label && (
                                                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-1 text-center">
                                                        {img.label}
                                                    </div>
                                                )}
                                                <div
                                                    className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 text-xs rounded flex items-center gap-1 cursor-move"
                                                    draggable
                                                    onDragStart={(e) => handleImageDragStart(e, section.id, img.id)}
                                                    onDragEnd={handleImageDragEnd}
                                                >
                                                    <GripVertical className="h-3 w-3" />
                                                </div>
                                            </div>
                                            <Input
                                                placeholder="Image label (optional)"
                                                value={img.label}
                                                onChange={(e) => updateImageLabel(section.id, img.id, e.target.value)}
                                                className="text-sm"
                                            />
                                            {(() => {
                                                const fit: FitMode = (img.fitMode ?? 'contain') as FitMode;
                                                const aspectHW = getAspectHW(img);
                                                const presets: Array<{ label: string; w: number; h: number }> = [
                                                    { label: '16:9', w: 16, h: 9 },
                                                    { label: '4:3', w: 4, h: 3 },
                                                    { label: '1:1', w: 1, h: 1 },
                                                    { label: '3:4', w: 3, h: 4 },
                                                ];
                                                return (
                                                    <div className="space-y-2 rounded-xl border bg-slate-50/80 p-2.5">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <Label className="sr-only">Display mode</Label>
                                                            <select
                                                                value={fit}
                                                                onChange={(e) => applyFitMode(section.id, img.id, e.target.value as FitMode)}
                                                                className="h-9 min-w-[10rem] flex-1 rounded-md border bg-white px-2 text-sm"
                                                            >
                                                                <option value="contain">Contain (fit within bounds)</option>
                                                                <option value="cover">Cover (fill frame, crop if needed)</option>
                                                                <option value="actual">Actual (natural aspect ratio)</option>
                                                            </select>
                                                            <Button
                                                                type="button"
                                                                variant={isAdjusting ? 'default' : 'outline'}
                                                                size="sm"
                                                                className="h-9"
                                                                onClick={() => setActiveAdjustImage(isAdjusting ? null : { sectionId: section.id, imageId: img.id })}
                                                            >
                                                                {isAdjusting ? 'Done' : 'Adjust'}
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-9"
                                                                onClick={() => resetImageLayout(section.id, img.id)}
                                                            >
                                                                Reset
                                                            </Button>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                                            <span className="shrink-0">Aspect</span>
                                                            <div className="flex gap-1">
                                                                {presets.map(p => {
                                                                    const targetHW = p.h / p.w;
                                                                    const active = Math.abs(aspectHW - targetHW) < 0.01;
                                                                    return (
                                                                        <Button
                                                                            key={p.label}
                                                                            type="button"
                                                                            variant={active ? 'default' : 'outline'}
                                                                            size="sm"
                                                                            className="h-7 px-2 text-[11px]"
                                                                            onClick={() => updateImageDisplay(section.id, img.id, { aspectRatio: clampAspectHW(targetHW) })}
                                                                        >
                                                                            {p.label}
                                                                        </Button>
                                                                    );
                                                                })}
                                                            </div>
                                                            <input
                                                                type="range"
                                                                min={MIN_ASPECT_HW}
                                                                max={MAX_ASPECT_HW}
                                                                step={0.01}
                                                                value={aspectHW}
                                                                onChange={(e) => updateImageDisplay(section.id, img.id, { aspectRatio: clampAspectHW(Number(e.target.value)) })}
                                                                className="flex-1 accent-slate-900"
                                                                aria-label="Container height ratio"
                                                            />
                                                            <span className="w-20 text-right text-[11px] tabular-nums text-slate-500">
                                                                {(1 / aspectHW).toFixed(2)} : 1
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Checkbox
                                                        checked={img.watermark}
                                                        onCheckedChange={() => toggleImageWatermark(section.id, img.id)}
                                                    />
                                                    <Label className="text-sm">Watermark</Label>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => toggleImageSelection(section.id, img.id)}
                                                >
                                                    <X className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {!opts?.omitAvailableGrid && (
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <Label>Available Images</Label>
                                <Button size="sm" variant="outline" onClick={handleUploadClick}>
                                    <Upload className="h-4 w-4 mr-1" />
                                    Upload Images
                                </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                {availableImages.map((media: any) => (
                                    <div
                                        key={media.id}
                                        onClick={() => !media.uploading && !media.error && toggleImageSelection(section.id, media.id)}
                                        className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${media.uploading || media.error
                                            ? 'cursor-default border-border'
                                            : 'cursor-pointer border-border hover:border-primary/50'
                                            }`}
                                    >
                                        <div
                                            className="w-full h-full bg-cover bg-center"
                                            style={{ backgroundImage: `url(${getMediaUrl('path' in media ? media.path : '')})` }}
                                        />
                                        {media.uploading && (
                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                <Spinner />
                                            </div>
                                        )}
                                        {media.error && (
                                            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
                                                <X className="h-8 w-8 text-red-500" />
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        retryUpload(media);
                                                    }}
                                                >
                                                    Retry
                                                </Button>
                                            </div>
                                        )}
                                        {media.label && !media.uploading && !media.error && (
                                            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                                {media.label}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderDialogImageManager = () => {
        const selectedImages = dialogImages;
        const availableImages = (proposalListing.medias || []).filter(
            media => !selectedImages.some(img => img.id === media.id)
        );

        return (
            <div className="space-y-4">
                <Label>Section Images</Label>

                {selectedImages.length > 0 && (
                    <div>
                        <Label className="mb-3 block text-sm text-muted-foreground">Selected Images ({selectedImages.length})</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto">
                            {selectedImages.map((img) => {
                                const media = (proposalListing.medias || []).find(m => m.id === img.id);
                                if (!media) return null;

                                return (
                                    <div key={img.id} className="border rounded-lg p-2 space-y-2">
                                        <div className="relative aspect-video rounded overflow-hidden" style={{ containerType: 'inline-size' } as React.CSSProperties}>
                                            <div
                                                className="w-full h-full bg-cover bg-center"
                                                style={{ backgroundImage: `url(${getMediaUrl('path' in media ? media.path : '')})` }}
                                            />
                                            {img.watermark && <ProposalWatermark name={watermarkName} ren={watermarkRen} />}
                                        </div>
                                        <Input
                                            placeholder="Image label (optional)"
                                            value={img.label || ''}
                                            onChange={(e) => updateDialogImageLabel(img.id, e.target.value)}
                                            className="text-sm"
                                        />
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    checked={img.watermark}
                                                    onCheckedChange={() => toggleDialogImageWatermark(img.id)}
                                                />
                                                <Label className="text-sm">Watermark</Label>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => removeDialogImage(img.id)}
                                            >
                                                <X className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div>
                    <Label className="mb-3 block text-sm text-muted-foreground">Available Images</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-[200px] overflow-y-auto">
                        {
                            availableImages.map((media) => (
                                <div
                                key={media.id}
                                onClick={() => toggleDialogImage(media.id)}
                                className="relative aspect-video rounded-lg overflow-hidden cursor-pointer border-2 border-border hover:border-primary/50 transition-all">
                                    <div
                                    className="w-full h-full bg-cover bg-center"
                                    style={{ backgroundImage: `url(${getMediaUrl('path' in media ? media.path : '')})` }}/>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-semibold">Proposal Configuration</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Customize sections and content for your property proposal
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={openAddSectionDialog}>
                            <Plus size={16} className="mr-2" />
                            Add Section
                        </Button>
                    </div>
                </div>

       
                <div className="space-y-4">
                    {sections && sections.map((section) => (
                        <Card
                            key={section.id}
                            id={section.id}
                            className={`border-border transition-all ${draggedSection === section.id ? 'opacity-50' : ''
                                } ${dragOverSection === section.id ? 'border-primary border-2' : ''
                                }`}
                            onDragOver={(e) => handleSectionDragOver(e, section.id)}
                            onDragLeave={handleSectionDragLeave}
                            onDrop={(e) => handleSectionDrop(e, section.id)}
                        >
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 flex-1">
                                        <div
                                            className="cursor-grab text-muted-foreground active:cursor-grabbing"
                                            draggable
                                            title="Drag to reorder section"
                                            onDragStart={(e) => handleSectionDragStart(e, section.id)}
                                            onDragEnd={handleSectionDragEnd}
                                        >
                                            <GripVertical className="h-5 w-5" />
                                        </div>
                                        {section.isNew ? (
                                            <div className="flex-1 space-y-2">
                                                <Input
                                                    value={section.title}
                                                    onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                                                    placeholder="Enter section title..."
                                                    className="font-semibold"
                                                    autoFocus
                                                />
                                                <Textarea
                                                    value={section.description}
                                                    onChange={(e) => updateSection(section.id, 'description', e.target.value)}
                                                    placeholder="Enter section description..."
                                                    rows={2}
                                                />
                                                <Button
                                                    size="sm"
                                                    onClick={() => updateSection(section.id, 'isNew', false)}
                                                    disabled={!section.title}
                                                >
                                                    <Check className="h-4 w-4 mr-1" />
                                                    Save
                                                </Button>
                                            </div>
                                        ) : editingSectionId === section.id ? (
                                            <div className="flex gap-2 flex-1">
                                                <Input
                                                    value={section.title}
                                                    onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                                                    className="font-semibold"
                                                />
                                                <Button
                                                    size="sm"
                                                    onClick={() => setEditingSectionId(null)}
                                                >
                                                    <Check className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="flex-1">
                                                <CardTitle className="text-base">{section.title}</CardTitle>
                                                <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-1">
                                        {!section.isNew && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => toggleSection(section.id)}
                                            >
                                                {section.collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                            </Button>
                                        )}
                                        {section.editable && !section.type && !section.isNew && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => openEditSectionDialog(section)}
                                            >
                                                <Edit2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                        {section.deletable && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => deleteSection(section.id)}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            {!section.collapsed && !section.isNew && (
                                <CardContent>
                                    {editingSectionId === section.id && (
                                        <div className="mb-4">
                                            <Label className="mb-2">Description</Label>
                                            <Textarea
                                                value={section.description}
                                                onChange={(e) => updateSection(section.id, 'description', e.target.value)}
                                                rows={2}
                                            />
                                        </div>
                                    )}
                                    {renderSectionContent(section)}
                                </CardContent>
                            )}
                        </Card>
                    ))}
                </div>
                {/* Save button has moved to the proposal page's sticky footer.
                    Keeping a hidden ref so any focus-based behaviour still resolves. */}
                <input type="hidden" data-saving={String(isSaving)} />
            </div>
            <Dialog open={showSectionDialog} onOpenChange={closeDialog}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingSection?.id ? 'Edit Section' : 'Add New Section'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Section Title</Label>
                            <Input
                            value={editingSection?.title || ''}
                            onChange={(e) => setEditingSection({ ...editingSection, title: e.target.value })}
                            placeholder="e.g., Amenities, Nearby Places" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea
                                value={editingSection?.description || ''}
                                onChange={(e) => setEditingSection({ ...editingSection, description: e.target.value })}
                                placeholder="Brief description of this section"
                                rows={3}
                            />
                        </div>
                        {renderDialogImageManager()}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}>
                            Cancel
                        </Button>
                        <Button onClick={saveSectionDialog} disabled={!editingSection?.title}>
                            {editingSection?.id ? 'Save Changes' : 'Add Section'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
            />
        </div>
    );
};

type ImageEditorProps = {
    src: string;
    alt: string;
    img: SectionImage;
    /** Container aspect ratio expressed as height / width. */
    aspectHW: number;
    onAspectHWChange: (value: number) => void;
    focused: boolean;
    onToggleFocus: () => void;
    onLoadNatural: (src: string, w: number, h: number) => void;
    resolveTransform: (img: SectionImage, src: string, containerH: number, containerW?: number) => { widthPct: number; heightPct: number; leftPct: number; topPct: number };
    onMoveStart: (e: React.PointerEvent, containerEl: HTMLElement) => void;
    onCornerStart: (e: React.PointerEvent, corner: 'nw' | 'ne' | 'sw' | 'se', containerEl: HTMLElement) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onWheelZoom: (e: WheelEvent, containerEl: HTMLElement) => void;
    version: number;
};

const ImageEditor: React.FC<ImageEditorProps> = ({
    src,
    alt,
    img,
    aspectHW,
    onAspectHWChange,
    focused,
    onToggleFocus,
    onLoadNatural,
    resolveTransform,
    onMoveStart,
    onCornerStart,
    onPointerMove,
    onPointerUp,
    onWheelZoom,
    version,
}) => {
    const frameRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
    const heightDragRef = useRef<{ startY: number; startAspect: number; width: number } | null>(null);

    // Track frame size for accurate transform resolution.
    useEffect(() => {
        const el = frameRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => {
            const cr = entries[0]?.contentRect;
            if (cr) setContainerSize({ w: cr.width, h: cr.height });
        });
        ro.observe(el);
        const rect = el.getBoundingClientRect();
        setContainerSize({ w: rect.width, h: rect.height });
        return () => ro.disconnect();
    }, []);

    // Non-passive wheel listener so we can preventDefault and stop the page from scrolling.
    useEffect(() => {
        const el = frameRef.current;
        if (!el) return;
        const handler = (e: WheelEvent) => {
            if (!e.ctrlKey && !e.metaKey) return;
            onWheelZoom(e, el);
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler as EventListener);
    }, [onWheelZoom]);

    const t = resolveTransform(img, src, containerSize?.h ?? 1, containerSize?.w);
    void version; // re-render trigger only

    const clamp01 = (v: number) => Math.min(100, Math.max(0, v));
    const corner = (which: 'nw' | 'ne' | 'sw' | 'se'): React.CSSProperties => {
        const isLeft = which === 'nw' || which === 'sw';
        const isTop = which === 'nw' || which === 'ne';
        // Display the handles inside the visible frame even when the image extends beyond it
        // (e.g. cover fit mode). Drag math uses the real image rect so resize still tracks.
        const xRaw = isLeft ? t.leftPct : t.leftPct + t.widthPct;
        const yRaw = isTop ? t.topPct : t.topPct + t.heightPct;
        return {
            position: 'absolute',
            left: `calc(${clamp01(xRaw)}% - 7px)`,
            top: `calc(${clamp01(yRaw)}% - 7px)`,
            width: 14,
            height: 14,
        };
    };

    const beginHeightDrag = (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = frameRef.current?.getBoundingClientRect();
        if (!rect) return;
        heightDragRef.current = { startY: e.clientY, startAspect: aspectHW, width: rect.width };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    };
    const onHeightDragMove = (e: React.PointerEvent) => {
        const s = heightDragRef.current;
        if (!s) return;
        const dy = e.clientY - s.startY;
        const next = s.startAspect + dy / Math.max(1, s.width);
        onAspectHWChange(next);
    };
    const endHeightDrag = (e: React.PointerEvent) => {
        if (heightDragRef.current) {
            try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
            heightDragRef.current = null;
        }
    };

    return (
        // Outer wrapper: width 100% and padding-top sets height by aspect ratio.
        // This matches what the server template emits in the final HTML.
        <div className="relative w-full" style={{ paddingTop: `${aspectHW * 100}%` }}>
            <div
                ref={frameRef}
                className={`group/image-frame absolute inset-0 rounded-lg border border-slate-300 bg-white ${focused ? 'overflow-visible' : 'overflow-hidden'} ${focused ? 'cursor-move' : 'cursor-default'}`}
                onDoubleClick={onToggleFocus}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            >
                {/* Ghost layer: same image, dimmed; visible only when focused so user can see the parts outside. */}
                {focused && (
                    <img
                        src={src}
                        alt=""
                        draggable={false}
                        className="pointer-events-none absolute select-none"
                        style={{
                            left: `${t.leftPct}%`,
                            top: `${t.topPct}%`,
                            width: `${t.widthPct}%`,
                            height: `${t.heightPct}%`,
                            opacity: 0.3,
                            filter: 'grayscale(0.4)',
                        }}
                    />
                )}
                {/* Clipper: bright image, always overflow-hidden so inside-frame shows full opacity even when focused. */}
                <div className="absolute inset-0 overflow-hidden rounded-lg">
                    <img
                        src={src}
                        alt={alt}
                        draggable={false}
                        onLoad={e => {
                            const node = e.currentTarget as HTMLImageElement;
                            onLoadNatural(src, node.naturalWidth, node.naturalHeight);
                        }}
                        onPointerDown={e => {
                            const el = frameRef.current;
                            if (!el || !focused) return;
                            onMoveStart(e, el);
                        }}
                        className={`absolute select-none bg-white ${focused ? 'cursor-move' : ''}`}
                        style={{
                            left: `${t.leftPct}%`,
                            top: `${t.topPct}%`,
                            width: `${t.widthPct}%`,
                            height: `${t.heightPct}%`,
                            touchAction: 'none',
                        }}
                    />
                </div>
                {/* Boundary indicator when focused */}
                {focused && (
                    <div className="pointer-events-none absolute inset-0 rounded-lg border-2 border-dashed border-slate-500/70" aria-hidden />
                )}
                {/* Corner handles */}
                {focused && (['nw', 'ne', 'sw', 'se'] as const).map(c => (
                    <button
                        key={c}
                        type="button"
                        aria-label={`Resize from ${c}`}
                        className="z-30 rounded-full border-2 border-white bg-slate-900 shadow-md transition hover:scale-110"
                        style={{
                            ...corner(c),
                            cursor: c === 'nw' || c === 'se' ? 'nwse-resize' : 'nesw-resize',
                        }}
                        onPointerDown={e => {
                            const el = frameRef.current;
                            if (!el) return;
                            onCornerStart(e, c, el);
                        }}
                    />
                ))}
            </div>
            {/* Bottom drag handle: width-spanning grip lets the user pull the container taller/shorter. */}
            <div
                className="absolute left-1/2 z-40 flex h-3 w-24 -translate-x-1/2 cursor-ns-resize items-center justify-center rounded-full border border-slate-300 bg-white shadow-sm hover:bg-slate-50"
                style={{ bottom: -6 }}
                onPointerDown={beginHeightDrag}
                onPointerMove={onHeightDragMove}
                onPointerUp={endHeightDrag}
                onPointerCancel={endHeightDrag}
                title="Drag to resize container height"
                aria-label="Drag to resize container height"
            >
                <div className="h-0.5 w-10 rounded-full bg-slate-400" />
            </div>
        </div>
    );
};

export default ProposalConfiguration;
